import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import type { GroupFamily } from './grouping.types';

/** Cartography data of an item: tags by level (`level0` = country) and, for venues, the stored country. */
export interface CartographyItemInfo {
  country?: string | null;
  tags?: Record<string, string[] | undefined>;
}

/** Only screens and venues (POIs) can be grouped by cartographic area. */
export function supportsCartographyGrouping(family: GroupFamily): boolean {
  return family !== 'locations';
}

export const toCca3 = (country: string | null | undefined): string | null => {
  const cca3 = country ? normalizeCountryToCca3(country) : '';
  return cca3.length === 3 ? cca3.toUpperCase() : null;
};

/** CCA3 (upper case) of an item: screens `tags.level0`, venues `tags.level0` else `country`. */
export function itemCountry(family: GroupFamily, item: CartographyItemInfo): string | null {
  const tagged = item.tags?.level0?.[0];
  if (family === 'screens') {
    return toCca3(tagged);
  }
  return toCca3(tagged || item.country);
}

/** Country shared by every item (CCA3, upper case); `null` when empty, mixed, unknown or not supported. */
export function sharedCountry(family: GroupFamily, items: CartographyItemInfo[]): string | null {
  if (!supportsCartographyGrouping(family) || items.length === 0) {
    return null;
  }
  const countries = new Set(items.map(item => itemCountry(family, item)));
  const [country] = countries;
  return countries.size === 1 ? (country ?? null) : null;
}

/** Area code of an item at a cartography level (`tags[level][0]`), `null` when it has no area there. */
export function areaCodeOf(item: CartographyItemInfo, level: string): string | null {
  return item.tags?.[level]?.[0] ?? null;
}

/** Cartographic "New group" confirm: a level picked and at least one selected item with an area at it. */
export function canConfirmCartographyGroups({
  items,
  level
}: {
  items: CartographyItemInfo[];
  level: string | null;
}): boolean {
  return level !== null && items.some(item => areaCodeOf(item, level) !== null);
}

/** Level entry of a country's cartography dictionary (`tagsConfig`). */
export interface CartographyLevelConfig {
  id?: string | null;
  name?: string | null;
  order?: number | null;
}

export interface CartographyLevel {
  id: string;
  /** Dictionary name, used when there is no translation for the level. */
  name: string;
}

const EXCLUDED_LEVELS = new Set(['geoHash']);

/** Levels the user can group by: every configured level but `geoHash`, sorted by `order` (missing order last). */
export function selectableLevels(tagsConfig: (CartographyLevelConfig | null | undefined)[]): CartographyLevel[] {
  return tagsConfig
    .filter((config): config is CartographyLevelConfig & { id: string } =>
      Boolean(config?.id && !EXCLUDED_LEVELS.has(config.id))
    )
    .sort((a, b) => (a.order ?? Number.POSITIVE_INFINITY) - (b.order ?? Number.POSITIVE_INFINITY))
    .map(config => ({ id: config.id, name: config.name || config.id }));
}
